/**
 * Менеджер PvP боев
 * Вынесено из legacy-app.js для модульности
 * 
 * Управляет поиском противников, созданием матчей и обработкой PvP боев
 */

/**
 * @typedef {Object} UserCreatureId
 * @property {number|null} creatureId - ID базового существа
 * @property {number|null} userCreatureId - ID пользовательского существа
 */

/**
 * Парсит строку формата "user:creatureId:userCreatureId"
 * 
 * @param {string} value - Строка формата "user:creatureId:userCreatureId"
 * @returns {UserCreatureId} Объект с creatureId и userCreatureId
 * 
 * @example
 * parseUserCreatureId("user:123:456") // { creatureId: 123, userCreatureId: 456 }
 * parseUserCreatureId("user:123") // { creatureId: 123, userCreatureId: null }
 */
export function parseUserCreatureId(value) {
  const parts = value.split(":");
  const creatureId = parts[1] ? parseInt(parts[1], 10) : null;
  const userCreatureId = parts[2] ? parseInt(parts[2], 10) : null;
  return {
    creatureId: Number.isFinite(creatureId) ? creatureId : null,
    userCreatureId: Number.isFinite(userCreatureId) ? userCreatureId : null,
  };
}

/**
 * Получает имя пользователя из объекта пользователя
 * 
 * Проверяет различные поля объекта пользователя для получения имени:
 * username, telegram_username, first_name + last_name, name, nickname
 * 
 * @param {Object|null} currentUser - Объект текущего пользователя
 * @param {string} [currentUser.username] - Username пользователя
 * @param {string} [currentUser.telegram_username] - Telegram username
 * @param {string} [currentUser.telegramUsername] - Telegram username (альтернативное поле)
 * @param {string} [currentUser.first_name] - Имя
 * @param {string} [currentUser.last_name] - Фамилия
 * @param {string} [currentUser.name] - Имя (альтернативное поле)
 * @param {string} [currentUser.nickname] - Никнейм
 * @returns {string|null} Имя пользователя или null, если не найдено
 * 
 * @example
 * getUserName({ username: "john_doe" }) // "john_doe"
 * getUserName({ first_name: "John", last_name: "Doe" }) // "John Doe"
 * getUserName(null) // null
 */
export function getUserName(currentUser) {
  if (!currentUser) return null;
  
  return (
    currentUser.username ||
    currentUser.telegram_username ||
    currentUser.telegramUsername ||
    [currentUser.first_name, currentUser.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    currentUser.name ||
    currentUser.nickname ||
    null
  );
}

/**
 * Создает менеджер PvP боев
 * 
 * Управляет поиском противников, созданием матчей, опросом статуса
 * и обработкой результатов PvP боев.
 * 
 * @param {Object} params - Параметры менеджера
 * @param {PvpState} params.pvpState - Состояние PvP (H)
 * @param {Function} params.safeFetch - Функция безопасного fetch
 * @param {string} params.apiUrl - URL API
 * @param {Function} params.buildAuthHeaders - Функция построения заголовков авторизации
 * @param {Function} params.getTelegramWebAppSafe - Функция получения Telegram WebApp
 * @param {Function} params.loadBattleData - Функция загрузки данных боя (wt)
 * @param {Function} params.showBattleModal - Функция показа модального окна боя (ht)
 * @param {Function} params.renderBattleView - Функция рендеринга вида боя (vt)
 * @param {Function} params.renderBattleResult - Функция рендеринга результата боя (Bt)
 * @param {Function} params.loadBattleHistory - Функция загрузки истории боев (Ye)
 * @param {Function} params.checkErrorbornStatus - Функция проверки статуса Errorborn (Ee)
 * @param {Function} params.renderErrorbornScreen - Функция рендеринга экрана Errorborn (qe)
 * @param {Function} params.parseUserCreatureId - Функция парсинга ID существа
 * @param {Function} params.updatePvpUI - Функция обновления UI PvP (ze)
 * @returns {PvpManager} Объект с методами PvP менеджера
 * 
 * @example
 * const pvpManager = createPvpManager({
 *   pvpState: H,
 *   safeFetch: safeFetchWithEnhancements,
 *   apiUrl: apiUrl,
 *   // ... другие параметры
 * });
 * await pvpManager.startPvpSearch();
 */
export function createPvpManager({
  pvpState,
  safeFetch,
  apiUrl,
  buildAuthHeaders,
  getTelegramWebAppSafe,
  loadBattleData,
  showBattleModal,
  renderBattleView,
  renderBattleResult,
  loadBattleHistory,
  checkErrorbornStatus,
  renderErrorbornScreen,
  parseUserCreatureId,
  updatePvpUI,
}) {
  /**
   * Останавливает polling PvP
   * 
   * Очищает таймер опроса статуса матча.
   * 
   * @returns {void}
   */
  function stopPvpPolling() {
    if (pvpState.pollTimer) {
      clearTimeout(pvpState.pollTimer);
      pvpState.pollTimer = null;
    }
  }

  /**
   * Обновляет статус PvP и UI
   * 
   * @param {string} statusText - Текст статуса для отображения
   * @returns {void}
   */
  function updatePvpStatus(statusText) {
    pvpState.statusText = statusText;
    updatePvpUI();
  }

  /**
   * Обновляет UI элементов PvP
   * 
   * Обновляет состояние кнопок поиска/отмены и текста статуса
   * в зависимости от текущего состояния PvP.
   * 
   * @returns {void}
   */
  function updatePvpControls() {
    const searchBtn = document.getElementById("battle-pvp-search-btn");
    const cancelBtn = document.getElementById("battle-pvp-cancel-btn");
    const statusEl = document.getElementById("battle-pvp-status");
    const creatureSelect = document.getElementById("battle-my-creature");
    
    const hasCreature = !!(creatureSelect && creatureSelect.value && creatureSelect.value.startsWith("user:"));
    const isSearching = !!pvpState.queueId;
    
    if (searchBtn) {
      searchBtn.disabled = isSearching || !hasCreature;
    }
    
    if (cancelBtn) {
      cancelBtn.style.display = isSearching ? "block" : "none";
      cancelBtn.disabled = !isSearching;
    }
    
    if (statusEl) {
      statusEl.textContent =
        pvpState.statusText ||
        (isSearching ? "Поиск соперника..." : "PvP: поиск не активен");
    }
  }

  /**
   * Запускает polling для проверки статуса PvP матча
   * 
   * @param {number} [delay=3000] - Задержка перед следующим опросом (мс)
   * @returns {void}
   */
  function startPvpPolling(delay = 3000) {
    stopPvpPolling();
    pvpState.pollTimer = setTimeout(() => {
      pollPvpMatch().catch((error) => console.error("pollPvpMatch error:", error));
    }, delay);
  }

  /**
   * Обрабатывает найденный PvP матч
   * 
   * Загружает данные боя, показывает модальное окно,
   * рендерит вид боя и результат, обновляет историю боев.
   * 
   * @param {Object} matchData - Данные матча от сервера
   * @param {Object} [matchData.opponent] - Данные противника
   * @param {string} [matchData.opponent.username] - Username противника
   * @param {Object} [matchData.opponent.creature] - Существо противника
   * @returns {Promise<void>}
   */
  async function handlePvpMatch(matchData) {
    stopPvpPolling();
    pvpState.queueId = null;
    pvpState.startedAt = 0;
    
    const opponentName =
      matchData?.opponent?.username ||
      matchData?.opponent?.creature?.name ||
      "Соперник найден";
    
    updatePvpStatus(`Соперник: ${opponentName}`);
    
    try {
      loadBattleData(matchData);
      showBattleModal();
      renderBattleView();
      renderBattleResult(matchData);
      await loadBattleHistory();
      
      const errorbornStatus = await checkErrorbornStatus(true);
      const errorbornScreen = document.getElementById("errorborn-screen");
      if (errorbornScreen?.classList.contains("active") && errorbornStatus) {
        renderErrorbornScreen(errorbornStatus);
      }
    } catch (error) {
      console.error("handlePvpMatch error", error);
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Ошибка обработки PvP боя");
    } finally {
      updatePvpControls();
    }
  }

  /**
   * Опрашивает статус PvP матча
   * 
   * Отправляет запрос на сервер для проверки статуса матча.
   * Если матч найден - обрабатывает его, иначе продолжает опрос.
   * 
   * @returns {Promise<void>}
   */
  async function pollPvpMatch() {
    if (!pvpState.queueId) return;
    
    const queueId = pvpState.queueId;
    const response = await safeFetch(
      `${apiUrl}/battle/pvp/status?queueId=${encodeURIComponent(queueId)}`,
      {
        headers: buildAuthHeaders(),
      }
    );
    
    if (!response.success) {
      console.warn("pollPvpMatch error:", response.error);
      pvpState.queueId = null;
      pvpState.startedAt = 0;
      updatePvpStatus(response.error || "Ошибка поиска PvP");
      return;
    }
    
    const data = response.data;
    
    if (data.status === "matched") {
      await handlePvpMatch(data);
      return;
    }
    
    if (data.status === "waiting") {
      const elapsed = pvpState.startedAt
        ? Math.max(1, Math.round((Date.now() - pvpState.startedAt) / 1000))
        : 0;
      const queueSize = Number(data.queueSize || 1);
      const statusText =
        queueSize > 1
          ? `Поиск соперника...${elapsed ? ` (${elapsed}с)` : ""} • игроков в очереди: ${queueSize}`
          : `Ожидаем соперника${elapsed ? ` (${elapsed}с)` : ""} • вы пока один`;
      
      updatePvpStatus(statusText);
      startPvpPolling();
      return;
    }
    
    pvpState.queueId = null;
    pvpState.startedAt = 0;
    updatePvpStatus("Поиск остановлен");
  }

  /**
   * Запускает поиск PvP соперника
   * 
   * Проверяет выбранное существо, отправляет запрос на сервер
   * и начинает опрос статуса поиска.
   * 
   * @returns {Promise<void>}
   * @throws {Error} Если существо не выбрано или запрос не удался
   */
  async function startPvpSearch() {
    if (pvpState.queueId) return;
    
    const creatureSelect = document.getElementById("battle-my-creature");
    if (!creatureSelect || !creatureSelect.value || !creatureSelect.value.startsWith("user:")) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Выберите своего бойца для PvP.");
      return;
    }
    
    const { creatureId, userCreatureId } = parseUserCreatureId(creatureSelect.value);
    if (!userCreatureId) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Некорректный выбор бойца для PvP.");
      return;
    }
    
    updatePvpStatus("Отправляем запрос...");
    
    const response = await safeFetch(`${apiUrl}/battle/pvp/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...buildAuthHeaders() },
      body: JSON.stringify({ creatureId, userCreatureId }),
    });
    
    if (!response.success) {
      console.warn("startPvpSearch error:", response.error);
      updatePvpStatus(response.error || "Ошибка поиска PvP");
      return;
    }
    
    const data = response.data;
    
    if (data.status === "matched") {
      await handlePvpMatch(data);
      return;
    }
    
    if (data.status === "waiting") {
      pvpState.queueId = data.queueId;
      pvpState.startedAt = Date.now();
      const queueSize = Number(data.queueSize || 1);
      const statusText =
        queueSize > 1
          ? `Поиск соперника... • игроков в очереди: ${queueSize}`
          : "Поиск соперника... • вы пока один в очереди";
      
      updatePvpStatus(statusText);
      startPvpPolling();
      return;
    }
    
    updatePvpStatus("Поиск не запущен");
  }

  /**
   * Отменяет поиск PvP соперника
   * 
   * Останавливает опрос и отправляет запрос на сервер для отмены поиска.
   * 
   * @param {Object} [options={}] - Опции
   * @param {boolean} [options.silent=false] - Тихая отмена (без сообщений пользователю)
   * @returns {Promise<void>}
   */
  async function cancelPvpSearch({ silent = false } = {}) {
    if (!pvpState.queueId) {
      if (!silent) {
        updatePvpStatus("PvP: поиск не активен");
      }
      return;
    }
    
    const queueId = pvpState.queueId;
    stopPvpPolling();
    pvpState.queueId = null;
    pvpState.startedAt = 0;
    
    if (silent) {
      pvpState.statusText = "PvP: поиск не активен";
    } else {
      updatePvpStatus("Отмена поиска...");
    }
    
    const response = await safeFetch(`${apiUrl}/battle/pvp/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...buildAuthHeaders() },
      body: JSON.stringify({ queueId }),
    });
    
    if (!response.success) {
      console.warn("cancelPvpSearch error:", response.error);
      if (!silent) {
        updatePvpStatus("Поиск отменён (локально)");
      }
      return;
    }
    
    const data = response.data;
    
    if (data.status === "matched") {
      await handlePvpMatch(data);
      return;
    }
    
    if (silent) {
      updatePvpControls();
    } else {
      updatePvpStatus("Поиск отменён");
    }
  }

  return {
    startPvpSearch,
    cancelPvpSearch,
    handlePvpMatch,
    pollPvpMatch,
    updatePvpStatus,
    updatePvpControls,
    stopPvpPolling,
    startPvpPolling,
  };
}

